import { platform } from '@tencent/wphk-platform-adapter';

/**
 * 获取url指定参数的值
 * @param name url参数名
 * @param url url，默认为当前页面url
 */
export function getUrlParam(name: string, url?: string): string {
  const regex = new RegExp(`[?&]${encodeURIComponent(name)}=([^&]*)`);
  const match = regex.exec(url || platform.location?.href || '');
  return match ? decodeURIComponent(match[1] || '') : '';
}

/**
 * 获取URL hash部分的参数值
 * @param name 参数名
 * @param url URL字符串（默认为当前页面URL）
 * @returns 参数值（若不存在则返回空字符串）
 */
export function getUrlHash(name: string, url?: string): string {
  const hashUrl = url || platform.location?.href || '';
  const hashIndex = hashUrl.indexOf('#');
  if (hashIndex === -1) {
    return ''; // 无hash部分直接返回空字符串
  }
  const hashQuery = hashUrl.slice(hashIndex + 1);
  const regex = new RegExp(`(^|&)${name}=([^&]*)(&|$)`);
  const match = hashQuery.match(regex);
  return match ? decodeURIComponent(match[2] || '') : '';
}

/**
 * 给指定url添加参数
 * @param url url
 * @param name key
 * @param value value
 * @returns 添加参数后的url
 */
export function addUrlParam(url: string, name: string, value: string): string {
  return addUrlParams(url, { [name]: value });
}

/**
 * 同时添加多个 URL 参数
 * @param url URL 字符串
 * @param params 需要添加的参数对象
 */
export function addUrlParams(url: string, params: object): string {
  // 移除已存在的参数
  const paramNames = Object.keys(params);
  const cleanedUrl = removeUrlParams(url, paramNames);

  const separator = cleanedUrl.includes('?') ? '&' : '?';
  const paramsString = Object.entries(params)
    .map(
      ([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`,
    )
    .join('&');
  return `${cleanedUrl}${separator}${paramsString}`;
}

/**
 * 去除URL中指定的参数
 * @param url url
 * @param name 参数名
 */
export function removeUrlParam(url: string, name: string) {
  return removeUrlParams(url, [name]);
}

/**
 * 移除URL中指定的参数
 * @param url URL字符串
 * @param params 需要移除的参数列表
 */
export function removeUrlParams(url: string, params: string[]): string {
  return params.reduce((currentUrl, param) => {
    const regex = new RegExp(
      `([?&])${encodeURIComponent(param)}=[^&]*(&|$)`,
      'g',
    );
    return currentUrl.replace(regex, (match, p1, p2) => (p2 === '&' ? p1 : ''));
  }, url);
}

/**
 * 将URL参数字符串转换为对象
 * @param params URL参数字符串
 */
export function paramsToObject<T extends object = {}>(params: string): T {
  const result: Record<string, string> = {};
  params.replace(/([^&=]+)=([^&]*)/g, (_, key, value) => {
    result[decodeURIComponent(key)] = decodeURIComponent(value);
    return '';
  });
  return result as T;
}

/**
 * 将对象转换为URL参数字符串
 * @param object 需要转换的对象
 */
export function objectToParams(object: object): string {
  return Object.entries(object)
    .map(
      ([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`,
    )
    .join('&');
}

/**
 * 安全解码URL参数
 * @param str 需要解码的字符串
 * @returns 解码后的字符串
 */
export function safeDecodeURIComponent(str = '') {
  try {
    return decodeURIComponent(str);
  } catch (error) {
    console.warn(error);
    return str;
  }
}

/**
 * 获取url参数中的adtag值
 * @returns adtag
 */
export function getParamAdtag() {
  const tag = getUrlParam('hottag') || getUrlHash('hottag')
        || getUrlParam('adtag') || getUrlHash('adtag')
        || getUrlParam('ADTAG') || getUrlHash('ADTAG');
  return tag;
}

/**
 * 将对象序列化成 query string, 例如 {a: 1, b: 2} => a=1&b=2
 * @param {Object} obj 待序列化的对象
 */
export const serialize = function (obj: Record<string, any>): string {
  return Object.keys(obj)
    .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(obj[key])}`)
    .join('&');
};
